import React, { useState } from 'react';
import { Play } from 'lucide-react';
import classesImg from '../assets/images/classes.png';
import reportImg from '../assets/images/report.png';
import VideoModal from './landing/VideoModal';
import { APP_LINKS, OVERVIEW_VIDEO_ID } from '../data/site';

const OVERVIEW = {
  videoId: OVERVIEW_VIDEO_ID,
  role: 'Product tour',
  headline: 'AtadaBoard platform overview',
};

const Hero = () => {
  const [isTourOpen, setIsTourOpen] = useState(false);

  const scrollToDemo = () =>
    document.getElementById('demo-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <section className="ab-hero">
      <div className="ab-shell ab-hero__grid">
        <div className="ab-hero__copy">
          {/*
            SEO note: the previous h1 carried the exact phrase "school
            management software". The redesign moves that phrase into the
            paragraph directly below so the h1 can carry the positioning line,
            and it still appears in the <title>, meta description and the
            SoftwareApplication JSON-LD in public/index.html. Keep the phrase in
            this block if the copy is ever refreshed.
          */}
          <h1 className="ab-display ab-hero__title">
            Smarter school management <span>starts here</span>
          </h1>

          <p className="ab-lead ab-hero__sub">
            AtadaBoard is school management software that brings students, attendance, reports,
            billing and parent communication into one platform.
          </p>

          <div className="ab-hero__cta">
            <button type="button" className="ab-btn ab-btn--primary" onClick={scrollToDemo}>
              Book a demo
            </button>
            <button type="button" className="ab-btn ab-btn--secondary" onClick={() => setIsTourOpen(true)}>
              <Play size={17} strokeWidth={2} fill="currentColor" />
              Watch the tour
            </button>
          </div>
        </div>

        <div className="ab-hero__art">
          <div className="ab-frame ab-hero__frame">
            <div className="ab-frame__bar">
              <div className="ab-frame__dots">
                <span />
                <span />
                <span />
              </div>
              <div className="ab-frame__url">{APP_LINKS.web.replace('https://', '')}</div>
            </div>
            <img
              src={classesImg}
              alt="The AtadaBoard classes screen listing each class with its teacher, term, member count and progress"
              className="ab-frame__media"
              width="2304"
              height="1310"
              fetchPriority="high"
              decoding="async"
            />
          </div>

          {/*
            A real crop of the student report screen, layered over the main
            frame so the hero shows two parts of the product rather than one
            flat screenshot.
          */}
          <figure className="ab-hero__inset">
            <img
              src={reportImg}
              alt="A student report in AtadaBoard showing days present, days absent and teacher remarks"
              width="2080"
              height="1234"
              loading="lazy"
              decoding="async"
            />
          </figure>
        </div>
      </div>

      {isTourOpen && <VideoModal video={OVERVIEW} onClose={() => setIsTourOpen(false)} />}

      <style jsx>{`
        .ab-hero {
          position: relative;
          padding-block: clamp(3rem, 6vw, 6rem) clamp(4rem, 7vw, 7rem);
          overflow: clip;
        }

        /*
          A soft radial wash anchored behind the artwork rather than an even
          linear gradient, so the top of the page has depth without a band.
        */
        .ab-hero::before {
          content: '';
          position: absolute;
          inset: -30% -10% auto -10%;
          height: 150%;
          background: radial-gradient(
            72% 60% at 78% 22%,
            var(--ab-blue-50) 0%,
            rgba(238, 245, 251, 0) 68%
          );
          pointer-events: none;
        }

        .ab-hero__grid {
          position: relative;
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1.05fr);
          align-items: center;
          gap: clamp(2.5rem, 5vw, 4.5rem);
        }

        .ab-hero__title {
          /*
            Sized so the headline holds to two lines from tablet up: the first
            line takes "Smarter school management", the accent takes the second.
          */
          font-size: clamp(2.25rem, 3.55vw, 3.05rem);
          margin-bottom: 1.25rem;
        }

        /* Weight and color carry the emphasis, not a second typeface. */
        .ab-hero__title span { color: var(--ab-blue); }

        .ab-hero__sub {
          max-width: 42ch;
          margin-bottom: 2rem;
        }

        .ab-hero__cta {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
        }

        .ab-hero__art {
          position: relative;
          /* Bleeds slightly past the container so the composition is not boxed. */
          margin-right: calc(var(--ab-gutter) * -1);
        }

        .ab-hero__frame {
          transform: perspective(2000px) rotateY(-3deg);
        }

        .ab-hero__inset {
          position: absolute;
          left: -8%;
          bottom: -11%;
          width: 46%;
          margin: 0;
          aspect-ratio: 4 / 3;
          border-radius: var(--ab-r-inner);
          border: 1px solid var(--ab-line);
          background: var(--ab-paper);
          box-shadow: var(--ab-shadow-lg);
          overflow: hidden;
        }

        /* Cropped to the top-left of the report: its title and score table. */
        .ab-hero__inset img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: 0 0;
          display: block;
        }

        @media (max-width: 1024px) {
          .ab-hero__grid {
            grid-template-columns: minmax(0, 1fr);
            gap: 3rem;
          }

          .ab-hero__sub { max-width: 52ch; }

          .ab-hero__art { margin-right: 0; }

          .ab-hero__frame { transform: none; }

          .ab-hero__inset {
            width: 38%;
            left: auto;
            right: 3%;
            bottom: -7%;
          }
        }

        @media (max-width: 640px) {
          .ab-hero {
            padding-block: 2.5rem 3.5rem;
          }

          .ab-hero__cta { flex-direction: column; align-items: stretch; }

          .ab-hero__inset { display: none; }
        }
      `}</style>
    </section>
  );
};

export default Hero;
